import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Phone, MapPin, Play, Check, ChevronDown, History, Wrench, Navigation } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ORDER_TYPE, relDay, fmtDate, fmtHours } from "@/lib/format";
import { OrderStatus, Priority, Chip, DocStatus } from "@/components/drift/StatusBadge";
import { EmptyState, LoadingRows } from "@/components/drift/common";
import { ReportSection, TimeSection, MaterialSection, KmSection, PhotoSection, ChecklistSection, BriefSection } from "@/components/drift/Execution";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const Card = ({ children, className, testid }) => <section className={cn("rounded-sm border border-border bg-card p-4", className)} data-testid={testid}>{children}</section>;

export default function MontorOrder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [o, setO] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showHist, setShowHist] = useState(false);
  const [done, setDone] = useState(false);

  const load = useCallback(() => api.get(`/drift/orders/${id}`).then((r) => setO(r.data)).catch((e) => setErr(e)), [id]);
  useEffect(() => { load(); }, [load]);
  const onUpdated = (next) => setO((p) => ({ ...p, ...next }));

  const start = async () => {
    setBusy(true);
    try { const r = await api.patch(`/drift/orders/${o.id}`, { status: "paagaar" }); onUpdated(r.data); toast.success("Oppdraget er starta"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const complete = async () => {
    setBusy(true);
    try { const r = await api.post(`/drift/orders/${o.id}/complete`); onUpdated(r.data); setDone(true); window.scrollTo({ top: 0, behavior: "smooth" }); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (err) return <div className="p-4"><EmptyState title={err?.response?.status === 403 ? "Oppdraget er ikkje tildelt deg" : "Fann ikkje oppdraget"} action={<Button variant="outline" asChild><Link to="/drift/montor">Til mine oppdrag</Link></Button>} /></div>;
  if (!o) return <div className="p-4"><LoadingRows n={8} /></div>;

  const hours = o.totals?.hours || 0;
  const canComplete = !!o.report && hours > 0;
  const isDone = o.status === "ferdig" || o.status === "fakturert";
  const phone = o.contact_phone || o.customer?.phone;
  const address = o.location_address || o.location?.address;

  if (done) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center px-6 text-center" data-testid="montor-complete-success">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-600 text-white"><Check className="h-8 w-8" /></span>
        <h1 className="mt-6 font-display text-3xl font-semibold tracking-tight">Oppdraget er fullført.</h1>
        <p className="mt-3 text-muted-foreground">Kontoret har fakturagrunnlaget. Dokumentasjonen vert kontrollert automatisk.</p>
        <div className="mt-6 w-full rounded-sm border border-border bg-card p-4 text-left text-sm">
          <p className="font-medium">{o.title}</p>
          <p className="text-muted-foreground">{o.customer_name}</p>
          <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            {[["Timar", fmtHours(hours)], ["Materiell", `${o.materials.length}`], ["Km", `${o.km || 0}`]].map(([l, v]) => <div key={l} className="rounded-sm bg-accent p-2"><p className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</p><p className="mt-0.5 font-mono text-lg tabular">{v}</p></div>)}
          </div>
        </div>
        <Button asChild className="mt-8 h-12 w-full rounded-sm bg-brand text-white hover:bg-brand-dark"><Link to="/drift/montor" data-testid="montor-back-to-list-button">Til mine oppdrag</Link></Button>
      </div>
    );
  }

  return (
    <div className="px-4 pt-4" data-testid="montor-order-page">
      <Link to="/drift/montor" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="montor-order-back-link"><ArrowLeft className="h-4 w-4" /> Mine oppdrag</Link>

      <div className="mt-4">
        <div className="flex flex-wrap items-center gap-2"><span className="font-mono text-xs text-muted-foreground">{o.ref}</span><OrderStatus status={o.status} /><Priority priority={o.priority} hideNormal /><Chip>{ORDER_TYPE[o.type]}</Chip></div>
        <h1 className="mt-2 font-display text-2xl font-semibold leading-tight tracking-tight" data-testid="montor-order-title">{o.title}</h1>
        <p className="mt-1 font-mono text-sm text-muted-foreground">{relDay(o.scheduled_date)}{o.scheduled_time ? ` · ${o.scheduled_time}` : ""}{o.estimated_hours ? ` · ca. ${o.estimated_hours} t` : ""}</p>
      </div>

      <Card className="mt-4" testid="montor-customer-card">
        <p className="text-[15px] font-medium">{o.customer_name}</p>
        {(o.contact_name || o.customer?.contact_name) && <p className="text-sm text-muted-foreground">{o.contact_name || o.customer?.contact_name}</p>}
        <p className="mt-2 flex items-start gap-2 text-sm"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" /><span>{o.location_name}<br /><span className="text-muted-foreground">{address}</span></span></p>
        <div className="mt-3 grid grid-cols-2 gap-2">
          {phone ? <a href={`tel:${phone.replace(/\s/g, "")}`} className="flex h-12 items-center justify-center gap-2 rounded-sm bg-accent text-sm font-medium" data-testid="montor-call-button"><Phone className="h-4 w-4" /> Ring</a> : <span className="flex h-12 items-center justify-center rounded-sm bg-accent text-sm text-muted-foreground">Ingen telefon</span>}
          {address && <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`} target="_blank" rel="noreferrer" className="flex h-12 items-center justify-center gap-2 rounded-sm bg-accent text-sm font-medium" data-testid="montor-navigate-button"><Navigation className="h-4 w-4" /> Vegbeskriving</a>}
        </div>
      </Card>

      {o.description && <Card className="mt-3" testid="montor-description-card"><p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Oppdraget</p><p className="mt-1.5 whitespace-pre-wrap text-[15px] leading-relaxed">{o.description}</p></Card>}

      {!isDone && <Card className="mt-3"><BriefSection order={o} onUpdated={onUpdated} compact /></Card>}

      {(o.installations?.length > 0 || o.history?.length > 0) && (
        <Card className="mt-3" testid="montor-history-card">
          <button onClick={() => setShowHist(!showHist)} className="flex w-full items-center justify-between text-left" data-testid="montor-history-toggle">
            <span className="inline-flex items-center gap-2 text-sm font-medium"><History className="h-4 w-4 text-muted-foreground" /> Anlegget · {o.installations?.length || 0} installasjonar · {o.history?.length || 0} tidlegare oppdrag</span>
            <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", showHist && "rotate-180")} />
          </button>
          {showHist && (
            <div className="mt-3 space-y-3">
              {o.installations?.length > 0 && <div className="flex flex-wrap gap-1.5">{o.installations.map((i) => <Chip key={i.id}><Wrench className="mr-1 h-3 w-3" />{i.type} · {i.name}</Chip>)}</div>}
              {o.history?.length > 0 && <ul className="divide-y divide-border">{o.history.slice(0, 5).map((h) => <li key={h.id} className="py-2 text-sm"><div className="flex items-center justify-between gap-2"><span className="truncate">{h.title}</span><span className="shrink-0 font-mono text-xs text-muted-foreground">{fmtDate(h.completed_at || h.scheduled_date || h.created_at, false)}</span></div>{h.report?.funn && <p className="mt-0.5 text-xs text-muted-foreground">{h.report.funn}</p>}</li>)}</ul>}
            </div>
          )}
        </Card>
      )}

      {o.status === "planlagt" || o.status === "ny" ? (
        <Button onClick={start} disabled={busy} className="mt-4 h-14 w-full rounded-sm bg-brand text-base font-medium text-white hover:bg-brand-dark" data-testid="montor-start-button"><Play className="mr-2 h-5 w-5" /> Start oppdrag</Button>
      ) : null}

      <div className="mt-4 space-y-3" data-testid="montor-execution">
        <Card><ReportSection order={o} onUpdated={onUpdated} big readOnly={isDone} /></Card>
        <Card><TimeSection order={o} onUpdated={onUpdated} big readOnly={isDone} asUserId={user.role !== "montor" && o.assigned_to ? o.assigned_to : undefined} /></Card>
        <Card><MaterialSection order={o} onUpdated={onUpdated} big readOnly={isDone} /></Card>
        <Card><KmSection order={o} onUpdated={onUpdated} big readOnly={isDone} /></Card>
        <Card><PhotoSection order={o} onUpdated={onUpdated} big readOnly={isDone} /></Card>
        <Card><ChecklistSection order={o} onUpdated={onUpdated} readOnly={isDone} /></Card>
        {isDone && <Card testid="montor-done-card"><div className="flex items-center justify-between"><p className="text-sm font-medium">Oppdraget er {o.status === "fakturert" ? "fakturert" : "fullført"}</p><DocStatus status={o.doc_check?.status} /></div><p className="mt-1 text-xs text-muted-foreground">Fullført {fmtDate(o.completed_at)}. {o.doc_check?.oppsummering}</p></Card>}
      </div>

      {!isDone && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-card/95 backdrop-blur-sm">
          <div className="mx-auto flex max-w-[560px] items-center gap-3 px-4 py-3">
            <div className="min-w-0 flex-1 text-xs text-muted-foreground">
              <span className="font-mono text-foreground">{fmtHours(hours)}</span> · {o.materials.length} materiell · {o.photo_files?.length || 0} bilete
              {!canComplete && <span className="block truncate text-warn">{!o.report ? "Lag arbeidsrapport" : "Legg inn timar"} for å fullføre</span>}
            </div>
            <Button onClick={complete} disabled={!canComplete || busy} className="h-12 shrink-0 rounded-sm bg-emerald-600 px-5 text-base font-medium text-white hover:bg-emerald-500 disabled:opacity-50" data-testid="montor-complete-button"><Check className="mr-2 h-5 w-5" /> Fullfør oppdrag</Button>
          </div>
        </div>
      )}
    </div>
  );
}
